import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { ApiError } from 'api/errors'
import {
  fetchMidasMessages,
  handoverMidasConversation,
  MidasConversation,
  MidasMessage,
  MidasMessagesPage,
  sendMidasFeedback,
  sendMidasMessage,
  startMidasConversation
} from 'api/midas'

// Poll fast while Midas is writing, slowly while waiting on the Goldie team.
export const STREAMING_POLL_MS = 700
export const HANDED_OVER_POLL_MS = 5000
// A reply still streaming after this long is treated as failed client-side
// (the backend marks it error too), so a stuck worker can't poll forever.
export const STREAMING_TIMEOUT_MS = 90_000

const HANDED_OVER_STATUSES = ['open', 'pending', 'solved']

type ConversationState = Pick<
  MidasConversation,
  'id' | 'status' | 'handed_over_at'
>

function mergeMessages(
  current: MidasMessage[],
  incoming: MidasMessage[]
): MidasMessage[] {
  if (incoming.length === 0) return current
  const byId = new Map(current.map(message => [message.id, message]))
  incoming.forEach(message => byId.set(message.id, message))
  return Array.from(byId.values()).sort((a, b) => a.id - b.id)
}

// Everything up to the first unfinished message is settled, so re-fetch from
// just before it; otherwise only ask for what is new.
function pollCursor(messages: MidasMessage[]): number {
  const streaming = messages.find(message => message.status === 'streaming')
  if (streaming) return streaming.id - 1
  return messages.length ? messages[messages.length - 1].id : 0
}

export function useMidasChat(open: boolean) {
  const queryClient = useQueryClient()
  const conversationQuery = useQuery(
    ['midas', 'conversation'],
    startMidasConversation,
    { enabled: open, staleTime: Infinity, retry: false }
  )
  const conversationId = conversationQuery.data?.id

  const [rawMessages, setMessages] = useState<MidasMessage[]>([])
  const [conversation, setConversation] = useState<ConversationState>()
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string>()
  const [loadError, setLoadError] = useState(false)
  const [tick, setTick] = useState(0)
  const [catchUp, setCatchUp] = useState(0)
  const [closed, setClosed] = useState(false)
  const [timedOut, setTimedOut] = useState<ReadonlySet<number>>(new Set())
  const streamingSince = useRef(new Map<number, number>())

  const messages = useMemo(
    () =>
      timedOut.size === 0
        ? rawMessages
        : rawMessages.map(m =>
            m.status === 'streaming' && timedOut.has(m.id)
              ? { ...m, status: 'error' as const }
              : m
          ),
    [rawMessages, timedOut]
  )

  const messagesRef = useRef(messages)
  messagesRef.current = messages

  const applyPage = useCallback((page: MidasMessagesPage) => {
    setMessages(current => mergeMessages(current, page.messages ?? []))
    if (page.conversation) setConversation(page.conversation)
  }, [])

  useEffect(() => {
    if (conversationQuery.data) setConversation(conversationQuery.data)
  }, [conversationQuery.data])

  // Catch up whenever the panel opens (history on first open, team replies
  // that arrived while it was closed afterwards) or the customer retries.
  useEffect(() => {
    if (!open || !conversationId) return
    let cancelled = false
    fetchMidasMessages(conversationId, pollCursor(messagesRef.current))
      .then(page => {
        if (cancelled) return
        applyPage(page)
        setLoadError(false)
      })
      .catch(() => !cancelled && setLoadError(true))
    return () => {
      cancelled = true
    }
  }, [open, conversationId, catchUp, applyPage])

  const hasStreaming = messages.some(message => message.status === 'streaming')

  // Time out a reply that has been streaming (as first seen by this client)
  // for longer than STREAMING_TIMEOUT_MS.
  useEffect(() => {
    const now = Date.now()
    let nextDeadline: number | undefined
    messages.forEach(m => {
      if (m.status !== 'streaming') return
      if (!streamingSince.current.has(m.id)) {
        streamingSince.current.set(m.id, now)
      }
      const deadline =
        (streamingSince.current.get(m.id) ?? now) + STREAMING_TIMEOUT_MS
      nextDeadline = Math.min(nextDeadline ?? deadline, deadline)
    })
    if (nextDeadline === undefined) return
    const timer = setTimeout(() => {
      const expired = messagesRef.current.filter(
        m =>
          m.status === 'streaming' &&
          (streamingSince.current.get(m.id) ?? Infinity) +
            STREAMING_TIMEOUT_MS <=
            Date.now()
      )
      if (expired.length) {
        setTimedOut(current => {
          const next = new Set(current)
          expired.forEach(m => next.add(m.id))
          return next
        })
      }
    }, Math.max(0, nextDeadline - now))
    return () => clearTimeout(timer)
  }, [messages])
  const handedOver =
    !!conversation?.handed_over_at ||
    HANDED_OVER_STATUSES.includes(conversation?.status ?? '')

  const isClosed = closed || conversation?.status === 'closed'

  let pollDelay: number | undefined
  if (hasStreaming) pollDelay = STREAMING_POLL_MS
  else if (handedOver && !isClosed) pollDelay = HANDED_OVER_POLL_MS

  useEffect(() => {
    if (!open || !conversationId || !pollDelay) return
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const page = await fetchMidasMessages(
          conversationId,
          pollCursor(messagesRef.current)
        )
        if (!cancelled) applyPage(page)
      } catch {
        // A missed poll is retried on the next tick.
      } finally {
        if (!cancelled) setTick(n => n + 1)
      }
    }, pollDelay)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, conversationId, pollDelay, tick, applyPage])

  const send = useCallback(
    async (body: string) => {
      const text = body.trim()
      if (!text || !conversationId) return false
      setSending(true)
      setSendError(undefined)
      try {
        const result = await sendMidasMessage(conversationId, text)
        const created = [result.customer_message, result.midas_message]
        setMessages(current =>
          mergeMessages(
            current,
            created.filter((m): m is MidasMessage => !!m)
          )
        )
        return true
      } catch (err) {
        // A closed conversation rejects new messages; the customer can start
        // a new chat (a solved one reopens to the team server-side).
        if (err instanceof ApiError && err.status === 409) setClosed(true)
        else setSendError("Your message didn't send. Please try again.")
        return false
      } finally {
        setSending(false)
      }
    },
    [conversationId]
  )

  const startNewChat = useCallback(async () => {
    setSendError(undefined)
    try {
      const fresh = await startMidasConversation()
      queryClient.setQueryData(['midas', 'conversation'], fresh)
      setConversation(fresh)
      setMessages([])
      setTimedOut(new Set())
      streamingSince.current.clear()
      setClosed(false)
    } catch {
      setSendError("We couldn't start a new chat. Please try again.")
    }
  }, [queryClient])

  const handover = useCallback(async () => {
    if (!conversationId) return
    setSendError(undefined)
    try {
      const updated = await handoverMidasConversation(
        conversationId,
        'customer_requested'
      )
      setConversation(updated)
      applyPage(
        await fetchMidasMessages(
          conversationId,
          pollCursor(messagesRef.current)
        )
      )
    } catch {
      setSendError("We couldn't reach the team just now. Please try again.")
    }
  }, [conversationId, applyPage])

  const giveFeedback = useCallback(async (messageId: number, value: 1 | -1) => {
    const previous = messagesRef.current.find(m => m.id === messageId)
    const setFeedback = (feedback: 1 | -1 | null) =>
      setMessages(current =>
        current.map(m => (m.id === messageId ? { ...m, feedback } : m))
      )
    setFeedback(value)
    try {
      await sendMidasFeedback(messageId, value)
    } catch {
      setFeedback(previous?.feedback ?? null)
    }
  }, [])

  const retry = useCallback(() => {
    setLoadError(false)
    if (conversationQuery.isError) conversationQuery.refetch()
    else setCatchUp(n => n + 1)
  }, [conversationQuery])

  const lastCustomerBody = useMemo(
    () => [...messages].reverse().find(m => m.author_type === 'customer')?.body,
    [messages]
  )

  return {
    messages,
    conversation,
    handedOver: handedOver && !isClosed,
    closed: isClosed,
    loading: conversationQuery.isLoading,
    error: conversationQuery.isError || loadError,
    replying: sending || hasStreaming,
    sendError,
    lastCustomerBody,
    send,
    handover,
    startNewChat,
    giveFeedback,
    retry
  }
}
